// Type definitions for account and monthly tracking models

/**
 * @typedef {Object} Account
 * @property {string} name - Account name
 * @property {string} type - 'asset' or 'debt'
 * @property {string} id - Unique identifier
 * @property {string} [category] - Optional custom category tag (e.g. "Retirement") used for filtering
 * @property {boolean} [includeInFire] - Whether this asset counts toward the FIRE number
 *   (e.g. a kid's 529 plan wouldn't). Assets/debts only ever check this via
 *   `a.includeInFire !== false`, so it defaults to true for accounts created
 *   before this field existed.
 */

/**
 * @typedef {Object} Accounts
 * @property {Account[]} assets - Array of asset accounts
 * @property {Account[]} debts - Array of debt accounts
 */

/**
 * @typedef {Object} MonthlyEntry
 * @property {string} month - Month key (YYYY-MM)
 * @property {Object} values - Account values for this month
 * @property {number} totalAssets - Sum of all asset values
 * @property {number} totalDebts - Sum of all debt values
 * @property {number} netWorth - Total assets minus total debts
 * @property {string} timestamp - ISO timestamp
 */

/**
 * @typedef {Object} AccountData
 * @property {Accounts} accounts - Asset and debt accounts
 * @property {MonthlyEntry[]} monthlyData - Array of monthly entries
 * @property {string} [lastUpdated] - ISO timestamp of last update
 */

/**
 * Generate a unique ID for an account
 * @param {string} type - 'asset' or 'debt'
 * @returns {string}
 */
export const generateAccountId = (type) => {
  return `${type}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Create a new account object
 * @param {string} name - Account name
 * @param {string} type - 'asset' or 'debt'
 * @param {string} [category] - Optional custom category tag
 * @returns {Account}
 */
export const createAccount = (name, type, category = '') => {
  return {
    name: name.trim(),
    type,
    category: category ? category.trim() : '',
    includeInFire: true,
    id: generateAccountId(type)
  };
};

/**
 * Create a new monthly entry object
 * @param {string} month - Month key (YYYY-MM)
 * @param {Object} values - Account values
 * @param {number} totalAssets - Total assets
 * @param {number} totalDebts - Total debts
 * @param {number} netWorth - Net worth
 * @returns {MonthlyEntry}
 */
export const createMonthlyEntry = (month, values, totalAssets, totalDebts, netWorth) => {
  return {
    month,
    values,
    totalAssets,
    totalDebts,
    netWorth,
    timestamp: new Date().toISOString()
  };
};

/**
 * Default FIRE (Financial Independence, Retire Early) settings.
 * withdrawalRate is a decimal (0.04 = 4%, the traditional "4% rule").
 */
export const createDefaultFireSettings = () => ({
  annualExpenses: '',
  withdrawalRate: 0.04
});

/**
 * Create default empty accounts object
 * @returns {Accounts}
 */
export const createDefaultAccounts = () => {
  return {
    assets: [],
    debts: [],
    fireSettings: createDefaultFireSettings()
  };
};
